import React, { useState, useEffect, useCallback } from 'react';
import { CardDescription, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { 
  FileText, 
  Briefcase, 
  Receipt, 
  FileSignature, 
  Archive,
  Download,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Edit3,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { addActivity } from '../utils/activityTracker';
import jsPDF from 'jspdf';

interface RegisterItem {
  id: string;
  option: string;
  description: string;
}

interface CategoryData {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  options: string[];
  items: RegisterItem[];
}

const FoldersApp: React.FC = () => {
  const { memberstack, isLoading } = useAuth();
  const { t } = useI18n();
  
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [customInputMode, setCustomInputMode] = useState<Set<string>>(new Set());

  const initializeCategories = useCallback((): CategoryData[] => [
    {
      id: 'admin',
      name: t('folders.admin'),
      description: t('folders.admin.description'),
      icon: <FileText className="h-6 w-6" />,
      color: 'bg-blue-50 border-blue-200',
      options: [
        'admin.residence_certificate',
        'admin.pension_fund',
        'admin.health_insurance',
        'admin.daycare',
        'admin.car',
        'admin.official_documents',
        'admin.military_service',
        'admin.power_of_attorney',
        'admin.inheritance_contract'
      ],
      items: Array(12).fill(null).map((_, index) => ({
        id: `admin-${index}`,
        option: '',
        description: ''
      }))
    },
    {
      id: 'work',
      name: t('folders.work'),
      description: t('folders.work.description'),
      icon: <Briefcase className="h-6 w-6" />,
      color: 'bg-green-50 border-green-200',
      options: [
        'work.employment_contract',
        'work.old_contracts',
        'work.references',
        'work.certificates',
        'work.diplomas',
      ],
      items: Array(12).fill(null).map((_, index) => ({
        id: `work-${index}`,
        option: '',
        description: ''
      }))
    },
    {
      id: 'tax',
      name: t('folders.tax'),
      description: t('folders.tax.description'),
      icon: <Receipt className="h-6 w-6" />,
      color: 'bg-yellow-50 border-yellow-200',
      options: [
        'tax.wage_statement',
        'tax.public_transport',
        'tax.pillar3_confirmation',
        'tax.health_insurance',
        'tax.bank',
        'tax.daycare_certificate',
        'tax.health_costs',
        'tax.donations',
        'tax.rental_deposit',
        'tax.serafe'
      ],
      items: Array(12).fill(null).map((_, index) => ({
        id: `tax-${index}`,
        option: '',
        description: ''
      }))
    },
    {
      id: 'contracts',
      name: t('folders.contracts'),
      description: t('folders.contracts.description'),
      icon: <FileSignature className="h-6 w-6" />,
      color: 'bg-purple-50 border-purple-200',
      options: [
        'contracts.household_liability_insurance',
        'contracts.credit_card',
        'contracts.bank',
        'contracts.property_management',
        'contracts.municipal_utilities',
        'contracts.cleaning_company',
        'contracts.loyalty_card',
        'contracts.leasing',
        'contracts.warranties_invoices',
        'contracts.loans'
      ],
      items: Array(12).fill(null).map((_, index) => ({
        id: `contracts-${index}`,
        option: '',
        description: ''
      }))
    },
    {
      id: 'taxarchive',
      name: t('folders.taxArchive'),
      description: t('folders.taxArchive.description'),
      icon: <Archive className="h-6 w-6" />,
      color: 'bg-gray-50 border-gray-200',
      options: [
        'taxarchive.tax_2026',
        'taxarchive.tax_2025',
        'taxarchive.tax_2024',
        'taxarchive.tax_2023',
        'taxarchive.tax_2022',
        'taxarchive.tax_2021',
        'taxarchive.tax_2020',
        'taxarchive.tax_2019',
        'taxarchive.tax_2018',
        'taxarchive.tax_2017',
        'taxarchive.tax_2016',
        'taxarchive.tax_2015'
      ],
      items: Array(12).fill(null).map((_, index) => ({
        id: `taxarchive-${index}`,
        option: '',
        description: ''
      }))
    }
  ], [t]);

  useEffect(() => {
    const loadUserData = async () => {
      const initialCategories = initializeCategories();

      if (!memberstack) {
        const prefilledCategories = initialCategories.map(category => ({
          ...category,
          items: category.items.map((item, index) => {
            const defaultOption = category.options[index] || '';
            return {
              ...item,
              option: defaultOption,
              description: item.description || ''
            };
          })
        }));
        setCategories(prefilledCategories);
        setCategoriesLoaded(true);
        return;
      }
      
      try {
        const response = await memberstack.getMemberJSON();
        const data = response.data?.organization || {};

        const processedCategories = initialCategories.map(category => {
          if (data[category.id]) {
            return {
              ...category,
              items: category.items.map((item, index) => {
                const savedItem = data[category.id][index];
                return savedItem ? {
                  ...item,
                  option: savedItem.option || '',
                  description: savedItem.description || ''
                } : item;
              })
            };
          } else {
            return {
              ...category,
              items: category.items.map((item, index) => {
                const defaultOption = category.options[index] || '';
                return {
                  ...item,
                  option: defaultOption,
                  description: item.description || ''
                };
              })
            };
          }
        });
        setCategories(processedCategories);
        setCategoriesLoaded(true);
      } catch (error) {
        console.error('Failed to load user data from Memberstack:', error);
        const prefilledCategories = initialCategories.map(category => ({
          ...category,
          items: category.items.map((item, index) => {
            const defaultOption = category.options[index] || '';
            return {
              ...item,
              option: defaultOption,
              description: item.description || ''
            };
          })
        }));
        setCategories(prefilledCategories);
        setCategoriesLoaded(true);
      }
    };

    loadUserData();
  }, [memberstack, t, initializeCategories]);

  if (isLoading || !categoriesLoaded) {
    return (
      <div className="folders-loading">
        <div className="text-center">
          <div className="folders-spinner"></div>
          <p className="folders-loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const saveToMemberstack = async () => {
    if (!memberstack) return;

    const newUserData: any = {};
    categories.forEach(category => {
      newUserData[category.id] = category.items.map(item => ({
        option: item.option,
        description: item.description
      }));
    });

    try {
      await memberstack.updateMemberJSON({ json: { organization: newUserData } });
      
      await addActivity(memberstack, {
        type: 'ordner_update',
        action: t('activity.foldersUpdated'),
        target: t('folders.title'),
        details: { categories: Object.keys(newUserData) }
      });
    } catch (error) {
      console.error('Failed to save data to Memberstack:', error);
    }
  };

  const toggleCustomInputMode = (itemId: string) => {
    setCustomInputMode(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleOptionChange = (categoryId: string, itemId: string, value: string) => {
    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.map(item => 
            item.id === itemId ? { ...item, option: value } : item
          )
        };
      }
      return category;
    }));
    saveToMemberstack();
  };

  const handleCustomInputChange = (categoryId: string, itemId: string, value: string) => {
    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.map(item => 
            item.id === itemId ? { ...item, option: value } : item
          )
        };
      }
      return category;
    }));
    saveToMemberstack();
  };

  const handleDescriptionChange = (categoryId: string, itemId: string, value: string) => {
    const maxLines = 3;
    const maxCharsPerLine = 37;
    
    let lines = value.split('\n');
    
    if (lines.length > maxLines) {
      lines = lines.slice(0, maxLines);
    }
    
    lines = lines.map(line => line.substring(0, maxCharsPerLine));
    
    const newValue = lines.join('\n');

    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.map(item => 
            item.id === itemId ? { ...item, description: newValue } : item
          )
        };
      }
      return category;
    }));
    saveToMemberstack();
  };

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.currentTarget.classList.add('folders-dragging');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('folders-dragging');
    setDraggedItem(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, categoryId: string, targetIndex: number) => {
    e.preventDefault();
    if (!draggedItem) return;

    const draggedCategoryId = draggedItem.split('-')[0];
    if (draggedCategoryId !== categoryId) return;

    const draggedIndex = parseInt(draggedItem.split('-')[1]);
    
    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        const newItems = [...category.items];
        const draggedItemData = newItems[draggedIndex];
        newItems.splice(draggedIndex, 1);
        newItems.splice(targetIndex, 0, draggedItemData);
        
        return {
          ...category,
          items: newItems.map((item, index) => ({
            ...item,
            id: `${categoryId}-${index}`
          }))
        };
      }
      return category;
    }));
    
    saveToMemberstack();
  };

  const generatePDF = async (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    const doc = new jsPDF();
    const a4_height = 297;
    const a4_width = 210;
    const margin = 10;
    const num_registers = 12;
    const y_step = a4_height / num_registers;

    const logoImgTop = "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";
    
    doc.addImage(logoImgTop, 'PNG', margin, 0.5 * y_step - 3, 7, 7);

    let y = 1.0 * y_step;
    for (let i = 0; i < num_registers - 1; i++) {
      doc.line(0.33 * a4_width, y, a4_width - margin, y);
      y += y_step;
    }

    y = 0.5 * y_step;
    category.items.forEach(item => {
      const value = item.option ? t(item.option).toUpperCase() : '';
      doc.text(value, a4_width - margin, y, { align: "right" });
      y += y_step;
    });

    y = 0.5 * y_step;
    category.items.forEach(item => {
      const value = item.description;
      doc.text(value, 0.33 * a4_width, y, { align: "left" });
      y += y_step;
    });

    doc.save(`${category.name}.pdf`);

    if (memberstack) {
      await addActivity(memberstack, {
        type: 'register_generated',
        action: t('activity.registerGenerated'),
        target: category.name,
        details: { categoryId, itemCount: category.items.length }
      });
    }
  };

  return (
    <div className="folders-page">
      <div className="folders-container">
        <div className="folders-header">
          <h1 className="folders-title">{t('folders.title')}</h1>
          <p className="folders-subtitle">{t('folders.subtitle')}</p>
        </div>

        <div className="folders-card">
          {categories.map((category, categoryIndex) => {
            const isExpanded = expandedCategories.has(category.id);
            const filledItems = category.items.filter(item => item.option || item.description).length;
            
            return (
              <div key={category.id}>
                <button
                  className="folders-category-button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleCategory(category.id);
                  }}
                >
                  <div className="folders-category-header">
                    <div className="folders-category-info">
                      <div className="folders-category-icon-box">
                        <FileText className="folders-category-icon" />
                      </div>
                      <div>
                        <CardTitle className="folders-category-name">
                          {category.name}
                        </CardTitle>
                        <CardDescription className="folders-category-desc">
                          {category.description}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="folders-category-status">
                      <Badge variant="secondary" className="folders-badge-count">
                        {filledItems} / 12
                      </Badge>
                      {isExpanded ? (
                        <ChevronUp className="folders-chevron" />
                      ) : (
                        <ChevronDown className="folders-chevron" />
                      )}
                    </div>
                  </div>
                </button>
                
                {isExpanded && (
                  <div 
                    className="folders-expanded-content"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="folders-toolbar">
                      <p className="folders-instruction">{t('folders.dragDropInstruction')}</p>
                      <Button 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          generatePDF(category.id);
                        }}
                        className="font-light"
                      >
                        <Download className="icon-sm mr-2" />
                        {t('folders.generatePDF')}
                      </Button>
                    </div>
                    
                    <div className="folders-items-list">
                      {category.items.map((item, index) => (
                        <div 
                          key={item.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, item.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, category.id, index)}
                          className="folders-item"
                        >
                          <GripVertical className="folders-grip" />
                          
                          <div className="folders-item-content">
                            <div>
                              <Textarea
                                value={item.description}
                                onChange={(e) => handleDescriptionChange(category.id, item.id, e.target.value)}
                                placeholder={t('folders.description')}
                                className="folders-textarea"
                                rows={1}
                              />
                            </div>
                            
                            <div className="relative">
                              {customInputMode.has(item.id) ? (
                                <div className="folders-custom-input">
                                  <Input
                                    value={t(item.option)}
                                    onChange={(e) => handleCustomInputChange(category.id, item.id, e.target.value)}
                                    placeholder={t('folders.customInput')}
                                    className="folders-input"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => toggleCustomInputMode(item.id)}
                                    className="folders-input-button"
                                  >
                                    <X className="icon-sm" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="folders-select-wrapper">
                                  <Select
                                    value={item.option}
                                    onValueChange={(value) => handleOptionChange(category.id, item.id, value)}
                                  >
                                    <SelectTrigger className="folders-select">
                                      <SelectValue placeholder={t('folders.selectCategory')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {category.options.map((option) => (
                                        <SelectItem key={option} value={option} className="font-light">
                                          {option ? t(option) : t('folders.empty')}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => toggleCustomInputMode(item.id)}
                                    className="folders-input-button"
                                  >
                                    <Edit3 className="icon-sm" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {categoryIndex < categories.length - 1 && (
                  <div className="folders-divider"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FoldersApp;